import { modelCategories } from '../../config/models';

// Utility functions for chat functionality

export const getPlaceholderText = (model: string): string => {
  // 'auto' has no single model to name - keep the prompt neutral.
  if (model === 'auto') return 'Ask anything…';

  const modelInfo = modelCategories
    .flatMap(category => category.models)
    .find(m => m.value === model);

  return modelInfo ? `Message ${modelInfo.label}...` : 'Type a message...';
};

export const isNearBottom = (containerRef: React.RefObject<HTMLDivElement>): boolean => {
  if (containerRef.current) {
    const container = containerRef.current;
    const threshold = 100; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }
  return false;
};

export const isImageGenerationModel = (model: string): boolean => {
  return (
    model === 'gpt-image-1' ||
    model === 'gpt-image-2' ||
    model === 'powerai-image-1' ||
    model === 'flux-pro' ||
    model === 'flux-dev' ||
    model === 'black-forest-labs/FLUX.1-kontext-max' ||
    model === 'black-forest-labs/FLUX.2-pro' ||
    model === 'google/gemini-3.1-flash-image-preview'
  );
};

export const isPerplexityModel = (model: string): boolean => {
  return model === 'llama-3.1-sonar-large-128k-online' || 
         model === 'llama-3.1-sonar-huge-128k-online' ||
         model === 'sonar' ||
         model === 'sonar-pro' ||
         model === 'sonar-reasoning' ||
         model === 'sonar-reasoning-pro' ||
         model === 'sonar-deep-research';
};

export const shouldShowBrowserIcon = (model: string): boolean => {
  // 'auto' can trigger the agentic web-search loop, so surface the browser icon.
  return model === 'auto' || isPerplexityModel(model);
};

export const isLargeTable = (content: string): boolean => {
  // Check if content contains a table with many rows/columns
  const tableMatches = content.match(/\|[^\n]*\|/g);
  if (!tableMatches) return false;
  
  // Count rows and estimate columns
  const rowCount = tableMatches.length;
  const avgColumnsPerRow = tableMatches.reduce((sum, row) => 
    sum + (row.match(/\|/g)?.length || 0), 0) / rowCount;
  
  // Consider it large if it has many rows or many columns
  return rowCount > 20 || avgColumnsPerRow > 8 || (rowCount * avgColumnsPerRow > 100);
};

export const truncateFilename = (filename: string, maxLength: number = 20): string => {
  if (filename.length <= maxLength) return filename;
  
  const extension = filename.split('.').pop() || '';
  const nameWithoutExt = filename.slice(0, filename.lastIndexOf('.'));
  const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 4) + '...';
  
  return `${truncatedName}.${extension}`;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const isImageFile = (filename: string): boolean => {
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(filename);
};

export const processPerplexityText = (text: string): string => {
  // Remove citation numbers in square brackets
  return text.replace(/\[\d+\]/g, '');
};

export const processPerplexityCitations = (text: string): string[] => {
  // Extract citation numbers and return as array
  const matches = text.match(/\[\d+\]/g);
  return matches ? matches.map(match => match.replace(/[\[\]]/g, '')) : [];
};

export const sanitizeFilename = (filename: string): string => {
  // Replace brackets and other special characters that might cause issues in markdown links
  return filename
    .replace(/\[/g, '(')  // Replace [ with (
    .replace(/\]/g, ')'); // Replace ] with )
};